import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { api } from "../lib/api";
import { gaAddToCart, gaRemoveFromCart } from "../lib/analytics";

const CartContext = createContext(null);

const keyFor = (slug, variant, personalization) =>
  `${slug}||${variant || ""}||${(personalization || "").trim()}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("vvs_cart")) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("vvs_cart", JSON.stringify(items));
  }, [items]);

  const addItem = (product, qty = 1, variant = "", personalization = "") => {
    const pers = (personalization || "").trim();
    gaAddToCart({ ...product, variant }, qty);
    setItems((prev) => {
      const k = keyFor(product.slug, variant, pers);
      const existing = prev.find((i) => keyFor(i.slug, i.variant, i.personalization) === k);
      if (existing)
        return prev.map((i) =>
          keyFor(i.slug, i.variant, i.personalization) === k
            ? { ...i, quantity: i.quantity + qty }
            : i
        );
      return [
        ...prev,
        {
          slug: product.slug,
          name: product.name,
          price: product.price,
          image: product.images?.[0] || "",
          category_slug: product.category_slug,
          variant: variant || "",
          personalization: pers,
          sku: product.sku || "",
          in_stock: product.in_stock !== false,
          quantity: qty,
        },
      ];
    });
  };

  const updateQty = (slug, variant, qty, personalization = "") => {
    if (qty < 1) return removeItem(slug, variant, personalization);
    const pers = (personalization || "").trim();
    setItems((prev) =>
      prev.map((i) =>
        keyFor(i.slug, i.variant, i.personalization) === keyFor(slug, variant, pers)
          ? { ...i, quantity: qty }
          : i
      )
    );
  };

  const removeItem = (slug, variant, personalization = "") => {
    const pers = (personalization || "").trim();
    setItems((prev) => {
      const gone = prev.find(
        (i) => keyFor(i.slug, i.variant, i.personalization) === keyFor(slug, variant, pers)
      );
      if (gone) gaRemoveFromCart(gone, gone.quantity);
      return prev.filter((i) => keyFor(i.slug, i.variant, i.personalization) !== keyFor(slug, variant, pers));
    });
  };
  const clearCart = () => setItems([]);
  const replaceItems = (rows) => setItems(Array.isArray(rows) ? rows : []);

  // Live sync — cart rows live in localStorage and can hold prices/stock that
  // are days old. Refreshes price + in_stock from the never-cached live endpoint.
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);
  const syncLive = useCallback(async () => {
    const slugs = [...new Set(itemsRef.current.map((i) => i.slug))];
    if (!slugs.length) return;
    try {
      const { data } = await api.get("/products-live", { params: { slugs: slugs.join(",") } });
      const live = data?.items || {};
      setItems((prev) =>
        prev.map((i) =>
          live[i.slug] ? { ...i, price: live[i.slug].price, in_stock: live[i.slug].in_stock !== false } : i
        )
      );
    } catch {
      // network hiccup — keep the locally stored values
    }
  }, []);

  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const count = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, updateQty, removeItem, clearCart, replaceItems, syncLive, total, count }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
